import { useEffect, useId, useRef } from "react";
import { Link } from "react-router-dom";

const ITEM_BASE = "transition-colors duration-200 font-bold text-sm block px-4 py-2";
const ENABLED_ITEMS = '[role="menuitem"]:not([aria-disabled="true"])';

export default function NavMenu({
  open,
  onOpen,
  onToggle,
  onClose,
  onCloseSoon,
  isRtl,
  buttonClassName,
  buttonLabel,
  children,
  items,
  activePath,
  getDisabledTitle,
  soonLabel,
}) {
  const menuId = useId();
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const pendingFocusRef = useRef(null);

  const getEnabledItems = () =>
    panelRef.current ? Array.from(panelRef.current.querySelectorAll(ENABLED_ITEMS)) : [];

  const focusItem = (target) => {
    const enabled = getEnabledItems();
    if (enabled.length === 0) return;
    enabled[target === "last" ? enabled.length - 1 : 0].focus();
  };

  useEffect(() => {
    if (open && pendingFocusRef.current) {
      focusItem(pendingFocusRef.current);
      pendingFocusRef.current = null;
    }
  }, [open]);

  const openAndFocus = (target) => {
    // When already open the effect won't re-run, so focus directly.
    if (open) {
      focusItem(target);
    } else {
      pendingFocusRef.current = target;
      onOpen();
    }
  };

  const handleButtonKeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      openAndFocus(e.key === "ArrowDown" ? "first" : "last");
    } else if (e.key === "Escape" && open) {
      e.preventDefault();
      onClose();
    }
  };

  const handlePanelKeyDown = (e) => {
    const enabled = getEnabledItems();
    const index = enabled.indexOf(document.activeElement);
    const last = enabled.length - 1;
    let next = null;

    if (e.key === "ArrowDown") next = index < last ? index + 1 : 0;
    else if (e.key === "ArrowUp") next = index > 0 ? index - 1 : last;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = last;
    else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
      buttonRef.current?.focus();
      return;
    } else if (e.key === "Tab") {
      onClose();
      return;
    }

    if (next !== null && enabled.length > 0) {
      e.preventDefault();
      enabled[next].focus();
    }
  };

  return (
    <div className="relative menu-dropdown-wrapper z-50" onMouseEnter={onOpen} onMouseLeave={onCloseSoon}>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={buttonLabel}
        onClick={onToggle}
        onKeyDown={handleButtonKeyDown}
        className={buttonClassName}
      >
        {children}
      </button>

      {open && (
        <div
          ref={panelRef}
          id={menuId}
          role="menu"
          className={`menuwraper absolute ${isRtl ? "left-0" : "right-0"} top-full mt-4 w-56 bg-white border border-gray-300 rounded-md shadow-lg z-50`}
          onMouseEnter={onOpen}
          onMouseLeave={onCloseSoon}
          onKeyDown={handlePanelKeyDown}
        >
          {items.map((item, idx) =>
            item.disabled ? (
              <span
                key={`${idx}-${item.path}`}
                role="menuitem"
                aria-disabled="true"
                tabIndex={-1}
                title={getDisabledTitle(item.disabledReason)}
                className={`${ITEM_BASE} text-gray-400 cursor-not-allowed`}
              >
                {item.label}
                {item.disabledReason === "soon" && (
                  <span className="ml-1 text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-300 uppercase tracking-wide">
                    {soonLabel}
                  </span>
                )}
              </span>
            ) : (
              <Link
                key={`${idx}-${item.path}`}
                role="menuitem"
                to={item.path}
                state={item.state}
                aria-current={item.path === activePath ? "page" : undefined}
                onClick={onClose}
                className={`${ITEM_BASE} hover:bg-gray-100 focus-visible:bg-gray-100 focus-visible:outline-none ${item.path === activePath ? "text-green-700" : "text-gray-700"}`}
              >
                {item.label}
              </Link>
            )
          )}
        </div>
      )}
    </div>
  );
}
